<script lang="ts">
	import { browser } from '$app/environment';
	import { scrollGroupObserver } from '$lib/attachments/scrollGroupObserver';
	import PlaceExtras from '$lib/components/city/feature/PlaceExtras.svelte';
	import FullRouteStat from '$lib/components/city/features/route/FullRouteStat.svelte';
	import Breadcrumbs from '$lib/components/ui/Breadcrumbs.svelte';
	import ExpandedFeatureCard from '$lib/components/ui/ExpandedFeatureCard.svelte';
	import ImageGallery from '$lib/components/ui/ImageGallery.svelte';
	import { type TourPlayerState } from '$lib/components/ui/map/_other/tourPlayerState';
	import type { TourWikiCache } from '$lib/components/ui/map/_other/tourWikiCache';
	import GpsTourPlayer from '$lib/components/ui/map/GpsTourPlayer.svelte';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import PlaceContext from '$lib/components/ui/PlaceContext.svelte';
	import SpecificFeaturesSection from '$lib/components/ui/section/SpecificFeaturesSection.svelte';
	import RouteShareCardModal from '$lib/components/ui/share/RouteShareCardModal.svelte';
	import type { BreadcrumbLink } from '$lib/features/_other/breadcrumbLink';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import {
		CITY_FEATURE_PAGE_PER_ITEM_TYPES,
		CITY_FEATURE_TYPE_ICON_MAP,
		CITY_FEATURE_TYPE_LABELS,
		CITY_FEATURE_TYPE_SINGLE_LABELS,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { CoverImage, ImageDetails } from '$lib/features/image/imageDetails.type';
	import type { Route } from '$lib/features/route/route.type';
	import type { RouteSegment } from '$lib/features/route/routeSegment.type';
	import { ROAD_SURFACE_LABELS } from '$lib/features/route/routeStep/roadSurface.enum';
	import type { FullCommonRouteFields } from '$lib/features/tour/fullCommonRouteFields';
	import type { TourStop } from '$lib/features/tour/stop/tourStop.type';
	import { type Tour } from '$lib/features/tour/tour.type';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import {
		calculateElevationProfile,
		calculateSurfaceStats,
		getStreetViewUrl,
		metersToFormattedDistance,
		type ElevationProfile,
		type SurfaceStat
	} from '$lib/utils/geo.util';
	import { useBackButtonInterception } from '$lib/utils/history.util.svelte';
	import { secondsToFormattedHourMinutes } from '$lib/utils/time.util';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { MapLibreMap } from 'maplibre-gl';
	import { IsMounted } from 'runed';
	import type { Component } from 'svelte';
	import { fade, fly } from 'svelte/transition';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconArrowUp from '~icons/mdi/arrow-up';
	import IconClockTimeFourOutline from '~icons/mdi/clock-time-four-outline';
	import IconClose from '~icons/mdi/close';
	import IconCompass from '~icons/mdi/compass';
	import IconFormatQuoteClose from '~icons/mdi/format-quote-close';
	import IconFormatQuoteOpen from '~icons/mdi/format-quote-open';
	import IconStreetView from '~icons/mdi/google-street-view';
	import IconHeartCircle from '~icons/mdi/heart-circle';
	import IconLightbulb from '~icons/mdi/lightbulb';
	import IconLightbulbOnOutline from '~icons/mdi/lightbulb-on-outline';
	import IconMap from '~icons/mdi/map';
	import IconMapMarker from '~icons/mdi/map-marker';
	import IconMapMarkerPath from '~icons/mdi/map-marker-path';
	import IconPlay from '~icons/mdi/play';
	import IconQuestionMark from '~icons/mdi/question-mark';
	import IconRestart from '~icons/mdi/restart';
	import IconFootprints from '~icons/mdi/shoe-print';
	import IconTerrain from '~icons/mdi/terrain';
	import IconWalk from '~icons/mdi/walk';

	let {
		featureType,
		data,
		location,
		cityHeroImage,
		language,
		proTipsSectionTitle,
		breadcrumbs,
		otherRoutes,
		otherTypesOfRoutes,
		player = $bindable(),
		wikiCache,
		onExtraStats
	}: {
		featureType: CityFeatureType;
		data: FullCommonRouteFields;
		location: PlaceLocation;
		cityHeroImage: CoverImage;
		language: Language;
		proTipsSectionTitle: string;
		breadcrumbs: BreadcrumbLink[];
		otherRoutes: CityFeaturePreview[];
		otherTypesOfRoutes: CityFeaturePreview[];
		player: TourPlayerState;
		wikiCache: TourWikiCache;
		onExtraStats?: () => { label: string; Icon: Component; value: string }[];
	} = $props();

	const isMounted = new IsMounted();
	const mapExplanation: string =
		'Smoothly fly between our curated route stops, while the surrounding area is dynamically populated with Wikipedia photos, summaries, and historical landmarks along your way!';
	let activeStopIndex = $state<number>(0);
	let isMobileMapOpen = $state<boolean>(false);
	let mapInstance = $state<MapLibreMap | null>(null);
	let mapWrapperRef = $state<HTMLDivElement | null>(null);
	let isVirtualFullscreen = $state<boolean>(false);
	let isShareModalOpen = $state<boolean>(false);

	let shouldLoadMapComponent = $derived<boolean>(deviceScreen.isDesktop || isMobileMapOpen);
	const tour = $derived<Tour>(data.tour);
	const route = $derived<Route>(tour.route);
	const stops = $derived<TourStop[]>(tour.stops);
	const elevationProfile = $derived<ElevationProfile | null>(calculateElevationProfile(route.segments));
	const surfaceStats = $derived<SurfaceStat[]>(calculateSurfaceStats(route.segments));
	const genericShareStops = $derived(
		stops.map((s) => {
			const stopImg = s.images?.[0] ?? s.primary?.images?.[0];
			return {
				name: s.englishName,
				coordinates: s.primary?.place?.coordinates ?? s.place.coordinates,
				imageUrl: stopImg ? UrlUtil.getFullImageUrl(stopImg) : undefined
			};
		})
	);

	const observeStop = $derived(
		scrollGroupObserver({
			itemCount: stops.length,
			prioritizeLastItem: true,
			rootMargin: '-20% 0px -20% 0px',
			onActiveChange: (newIndex: number) => (activeStopIndex = newIndex)
		})
	);

	useBackButtonInterception({
		stateKey: 'mobile-map-open',
		active: () => isMobileMapOpen,
		enabled: () => deviceScreen.isMobile,
		onBack: () => (isMobileMapOpen = false),
		onForward: () => (isMobileMapOpen = true)
	});

	useBackButtonInterception({
		stateKey: 'tour-player-full-screen',
		active: () => player.isPlaying,
		onBack: () => exitTour(),
		onForward: () => startTour()
	});

	function handleMapStopClick(stopIndex: number) {
		activeStopIndex = stopIndex;

		const elementId = `stop-${activeStopIndex}`;
		const element = document.getElementById(elementId);
		if (!element) return;

		const offset = 80;
		const top = element.getBoundingClientRect().top + window.scrollY - offset;
		window.scrollTo({ top, behavior: 'instant' });
	}

	async function startTour(reset = false) {
		if (reset) player.distance = 0;
		player.isPlaying = true;
		isVirtualFullscreen = true;
		requestAnimationFrame(() => mapInstance?.resize());
	}

	function exitTour() {
		player.isPlaying = false;
		isVirtualFullscreen = false;

		if (document.fullscreenElement && document.exitFullscreen) {
			document.exitFullscreen().catch(() => {});
		}

		requestAnimationFrame(() => {
			mapInstance?.resize();
			mapInstance?.easeTo({
				pitch: 45,
				bearing: 0,
				padding: { top: 0, bottom: 0, left: 0, right: 0 },
				duration: 800
			});
		});
	}
</script>

<div class="container mx-auto px-4 py-14 md:py-16 lg:px-0">
	<Breadcrumbs items={breadcrumbs} textColour="light" />

	<div class="relative mt-5 flex flex-col items-start gap-8 lg:flex-row lg:gap-12">
		<div class="w-full flex-1">
			<div class="mb-6">
				{@render Introduction()}
			</div>
			<div class="mb-6 flex flex-col gap-8">
				{@render TheWhy()}
				{#if tour.proTips && tour.proTips.length > 0}
					{@render ProTips()}
				{/if}
			</div>

			<ul class="flex flex-col gap-16 lg:gap-20">
				{#each stops as stop, index (stop.englishName)}
					{const segmentToNextStop = $derived<RouteSegment | undefined>(tour.route.segments.at(index))}
					{const isLast = index === stops.length - 1}

					<li class="flex flex-row items-stretch gap-3" id="stop-{index}" {@attach observeStop(index)}>
						{@render Timeline(index, isLast, segmentToNextStop)}

						<div class="flex min-w-0 flex-1 flex-col">
							{@render StopContent(stop)}

							{#if segmentToNextStop}
								{@render ToNextStop(segmentToNextStop)}
							{/if}
						</div>
					</li>
				{/each}
			</ul>
		</div>

		{#if deviceScreen.isDesktop}
			<div class="sticky top-[10vh] h-[calc(100vh-6rem)] w-full flex-1">
				{#if !player.isPlaying}
					<div class="absolute top-2 left-2 z-100">
						<button class="btn btn-circle btn-sm" popovertarget="map-explanation-desktop" aria-label="Show map explanation">
							<IconQuestionMark class="text-lg opacity-70" />
						</button>
						<div popover id="map-explanation-desktop">
							<div class="flex flex-row gap-4 rounded-box bg-secondary/70 p-4 text-secondary-content">
								<IconLightbulb class="mt-0.5 shrink-0 text-xl text-gray-200" />
								<div class="flex flex-col gap-1 text-gray-200">
									<h3 class="text-sm leading-tight">Interactive Route Guide</h3>
									<p class="text-xs leading-relaxed text-gray-200/80">
										{mapExplanation}
									</p>
								</div>
							</div>
						</div>
					</div>
				{/if}
				{@render MapContainer()}
			</div>
		{/if}
	</div>

	<PlaceContext label={CITY_FEATURE_TYPE_LABELS[featureType]} {location} className="mt-12" />

	{#if otherRoutes.length > 0}
		{const MAX_ITEMS = 3}
		{const hasMore = $derived<boolean>(otherRoutes.length > MAX_ITEMS)}
		<SpecificFeaturesSection
			sectionClasses="w-full mt-8 md:mt-12"
			{featureType}
			items={otherRoutes.slice(0, MAX_ITEMS)}
			{location}
			header={{
				subText: `Check out other routes in ${location.city.label}`
			}}
			ctaText="view route"
			seeAll={hasMore
				? {
						nav: UrlUtil.toCityFeatures({ location, type: featureType }),
						text: `view all ${CITY_FEATURE_TYPE_LABELS[featureType].toLowerCase()}`
					}
				: undefined}
		/>
	{/if}

	{#if otherTypesOfRoutes.length > 0}
		{@render OtherTourTypesSection()}
	{/if}

	{@render MobileMapButton()}
	{#if deviceScreen.smallerThanDesktop && isMobileMapOpen}
		{@render MobileMapModal()}
	{/if}

	<RouteShareCardModal
		bind:isOpen={isShareModalOpen}
		title={tour.feature.englishName}
		cityName={location.city.label}
		countryName={location.country?.label}
		stops={genericShareStops}
		{cityHeroImage}
		stats={[
			{ label: 'Distance', value: metersToFormattedDistance(route.distanceMeters) },
			{ label: 'Time', value: secondsToFormattedHourMinutes(route.durationSeconds, { formatting: 'medium' }) }
		]}
	/>
</div>

{#snippet Introduction()}
	<div class="flex flex-col items-start gap-6">
		<div class="flex flex-wrap gap-2 text-xs font-bold tracking-widest text-base-content/70">
			{#each data.categories as category (category)}
				<span class="badge badge-secondary">{category.replaceAll('_', ' ').toUpperCase()}</span>
			{/each}
		</div>

		<header>
			<h1 class="text-4xl lg:text-6xl">
				{tour.feature.englishName}
			</h1>
			<span class="mt-2 block text-xl font-light tracking-normal text-base-content/70 md:mt-3">
				<i>{CITY_FEATURE_TYPE_SINGLE_LABELS[featureType]}</i>
			</span>
		</header>

		<p class="text-lg leading-relaxed font-light text-base-content/80 md:text-xl">
			{tour.feature.description}
		</p>

		{@render PostcardActionButton()}

		<div class="mt-2 flex w-full flex-col gap-4">
			<div class="flex w-full flex-wrap items-center justify-center gap-6 rounded-box bg-base-200 p-5 shadow-sm">
				<FullRouteStat label="Distance" Icon={IconMapMarkerPath} value={metersToFormattedDistance(route.distanceMeters)} />
				<FullRouteStat
					label="Time"
					Icon={IconClockTimeFourOutline}
					value={secondsToFormattedHourMinutes(route.durationSeconds, { formatting: 'medium' })}
				/>
				{#if onExtraStats}
					{const extraStats = $derived(onExtraStats())}
					{#each extraStats as stat (stat.label)}
						<FullRouteStat label={stat.label} Icon={stat.Icon} value={stat.value} />
					{/each}
				{/if}
			</div>

			<div class="grid w-full grid-cols-1 gap-4">
				{#if surfaceStats.length > 0}
					{@render SurfaceStats()}
				{/if}
				{#if elevationProfile}
					{@render ElevationStats()}
				{/if}
			</div>
		</div>
	</div>

	{#snippet PostcardActionButton()}
		<button
			type="button"
			class="group flex w-full items-center justify-between rounded-box border border-base-content/10 bg-base-200/50 p-3 transition-all duration-200 hover:border-primary/40 hover:bg-base-200 hover:shadow-md sm:p-3.5"
			onclick={() => (isShareModalOpen = true)}
		>
			<div class="flex items-center gap-3">
				<div
					class="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-content sm:size-10"
				>
					<IconCompass class="text-lg sm:text-xl" />
				</div>
				<div class="flex flex-col text-left">
					<span class="text-xs font-bold tracking-tight text-base-content transition-colors group-hover:text-primary sm:text-sm">
						View Route Postcard
					</span>
					<span class="text-[11px] text-base-content/60 sm:text-xs">
						{stops.length} visual stops across {location.city.label}
					</span>
				</div>
			</div>

			<div class="flex items-center gap-2">
				<div class="hidden -space-x-2 overflow-hidden sm:flex">
					{#each stops.slice(0, 4) as stop}
						{const img = $derived<ImageDetails | undefined>(stop.images?.at(0) ?? stop.primary?.images?.at(0))}
						{#if img !== undefined}
							<img
								class="inline-block size-6 rounded-full object-cover ring-2 ring-base-200"
								src={UrlUtil.getFullImageUrl(img)}
								alt={stop.englishName}
							/>
						{/if}
					{/each}
				</div>

				<span class="btn btn-circle btn-ghost btn-xs group-hover:bg-primary/10 group-hover:text-primary sm:btn-sm">
					<IconArrowRight class="text-sm transition-transform duration-200 group-hover:translate-x-0.5" />
				</span>
			</div>
		</button>
	{/snippet}

	{#snippet SurfaceStats()}
		<div class="card w-full bg-base-100 shadow-sm card-border">
			<div class="card-body">
				<h2 class="mb-2 card-title text-base-content/70">
					<IconFootprints class="text-lg" />
					SURFACE BREAKDOWN
				</h2>

				<div class="flex h-3 w-full overflow-hidden rounded-full bg-base-300">
					{@render ProgressBar()}
				</div>

				<div class="mt-4 flex flex-wrap gap-x-6 gap-y-3">
					{@render Legend()}
				</div>
			</div>
		</div>

		{#snippet ProgressBar()}
			{#each surfaceStats as stat (stat.surface)}
				<div
					class="h-full border-r border-base-100 transition-all duration-500 last:border-r-0"
					style:width="{stat.percentage}%"
					style:background-color={stat.color}
					title="{ROAD_SURFACE_LABELS[stat.surface]}: {stat.percentage.toFixed(1)}%"
				></div>
			{/each}
		{/snippet}

		{#snippet Legend()}
			{#each surfaceStats as stat (stat.surface)}
				<div class="flex items-center gap-2 text-sm font-medium text-base-content/80">
					<span class="h-3 w-3 rounded-full shadow-sm" style:background-color={stat.color}></span>
					<span class="font-mono">{ROAD_SURFACE_LABELS[stat.surface]}</span>
					<span class="font-mono text-sm text-base-content/70">{stat.percentage.toFixed(0)}%</span>
				</div>
			{/each}
		{/snippet}
	{/snippet}

	{#snippet ElevationStats()}
		<div class="card w-full bg-base-100 shadow-sm card-border">
			<div class="card-body">
				<div class="mb-4 flex flex-col justify-between md:flex-row md:items-center">
					{@render Header()}
				</div>

				<div class="relative mt-2 h-20 w-full">
					<div class="absolute inset-0 flex flex-col justify-between">
						{@render TopLine()}
						{@render MidLine()}
						{@render BottomLine()}
					</div>
					{@render SparkLine()}
					{@render StopMarkers()}
				</div>
			</div>
		</div>

		{#snippet Header()}
			<h2 class="card-title text-base-content/70">
				<IconTerrain class="text-lg" />
				ELEVATION PROFILE
			</h2>
			<div class="badge badge-sm badge-info">
				<IconArrowUp />
				{Math.round(elevationProfile!.gain)}m gain
			</div>
		{/snippet}

		{#snippet TopLine()}
			<div class="relative w-full border-t border-dashed border-base-content/10">
				<span class="absolute -top-4.5 right-0 bg-base-100 pl-1 font-mono text-xs text-base-content/70">
					{Math.round(elevationProfile!.max)}m
				</span>
			</div>
		{/snippet}

		{#snippet MidLine()}
			<div class="w-full border-t border-dashed border-base-content/5"></div>
		{/snippet}

		{#snippet BottomLine()}
			<div class="relative w-full border-t border-solid border-base-content/20">
				<span class="absolute right-0 -bottom-4.5 bg-base-100 pl-1 font-mono text-xs text-base-content/70">
					{Math.round(elevationProfile!.min)}m
				</span>
			</div>
		{/snippet}

		{#snippet SparkLine()}
			<svg class="absolute inset-0 h-full w-full overflow-visible" preserveAspectRatio="none" viewBox="0 0 100 100">
				<defs>
					<linearGradient id="elevGradient" x1="0" x2="0" y1="0" y2="1">
						<stop offset="0%" stop-color="currentColor" stop-opacity="0.4" />
						<stop offset="100%" stop-color="currentColor" stop-opacity="0.0" />
					</linearGradient>
				</defs>
				<polygon points="0,100 {elevationProfile!.svgPoints} 100,100" fill="url(#elevGradient)" class="text-primary" />
				<polyline
					points={elevationProfile!.svgPoints}
					fill="none"
					stroke="currentColor"
					stroke-width="2"
					class="text-primary"
					stroke-linecap="round"
					stroke-linejoin="round"
					vector-effect="non-scaling-stroke"
				/>
			</svg>
		{/snippet}

		{#snippet StopMarkers()}
			{#if elevationProfile?.stops}
				<div class="pointer-events-none absolute inset-0">
					{#each elevationProfile.stops as stop (stop.index)}
						<div class="absolute inset-y-0 flex -translate-x-1/2 flex-col items-center" style:left="{stop.x}%">
							<div
								class="pointer-events-auto badge size-4.5 rounded-full p-0 font-mono badge-xs text-[9px] font-bold shadow-xs transition-transform badge-neutral hover:scale-125"
								title={`Stop ${stop.index}`}
							>
								{stop.index}
							</div>

							<div class="h-full w-px border-l border-dashed border-base-content/30"></div>
						</div>
					{/each}
				</div>
			{/if}
		{/snippet}
	{/snippet}
{/snippet}

{#snippet TheWhy()}
	<div class="card bg-primary/10 text-primary shadow-sm card-border">
		<div class="card-body md:p-8">
			<h2 class="card-title">
				<IconHeartCircle class="text-lg" />
				WHY WE LOVE THIS ROUTE
			</h2>

			<div class="relative mt-2">
				<IconFormatQuoteOpen
					class="absolute top-0 left-0 -translate-x-2 -translate-y-2 text-4xl text-primary/20 md:-translate-x-4 md:-translate-y-4 md:text-5xl"
				/>
				<p class="relative px-10 leading-relaxed font-medium text-primary/70">
					<i>{tour.why}</i>
				</p>
				<IconFormatQuoteClose
					class="absolute right-0 bottom-0 translate-x-2 translate-y-2 text-4xl text-primary/20 md:translate-x-4 md:translate-y-4 md:text-5xl"
				/>
			</div>
		</div>
	</div>
{/snippet}

{#snippet ProTips()}
	<div class="card bg-info/10 text-info-content/80 shadow-sm card-border">
		<div class="card-body md:p-8">
			<h2 class="card-title">
				<IconLightbulbOnOutline class="text-lg" />
				{proTipsSectionTitle.toUpperCase()}
			</h2>

			<ul class="space-y-1">
				{#each tour.proTips as tip (tip)}
					<li class="flex items-start gap-3">
						<span class="mt-1 text-info">•</span>
						<span class="leading-relaxed text-info-content/70">{tip}</span>
					</li>
				{/each}
			</ul>
		</div>
	</div>
{/snippet}

{#snippet Timeline(index: number, isLast: boolean, segmentToNextStop: RouteSegment | undefined)}
	<div class="flex flex-col items-center">
		<div
			class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-content shadow-sm md:size-9 md:text-base"
		>
			{index + 1}
		</div>

		{#if segmentToNextStop}
			<div class="w-0.5 flex-1 rounded-full bg-base-content/15"></div>
			<div class="flex size-6 shrink-0 items-center justify-center rounded-full bg-base-200">
				<IconWalk class="text-xs text-base-content/70" />
			</div>
		{:else if !isLast}
			<div class="my-2 w-0.5 flex-1 rounded-full bg-base-content/15"></div>
		{/if}
	</div>
{/snippet}

{#snippet StopContent(stop: TourStop)}
	{#if stop.primary}
		{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[stop.primary.type])}
		{const label = $derived<string>(CITY_FEATURE_TYPE_SINGLE_LABELS[stop.primary.type])}
		<div class="mb-1.5 flex items-center gap-1.5 text-xs font-bold tracking-widest text-primary">
			<Icon class="text-sm" />
			<span>{label.toLocaleUpperCase()}</span>
		</div>
	{:else}
		<div class="mb-1.5 flex items-center gap-1.5 text-xs font-bold tracking-widest text-base-content/70">
			<IconMapMarker class="text-sm" />
			<span>WAYPOINT</span>
		</div>
	{/if}

	<h2 class="mb-1 text-2xl text-base-content md:text-3xl">{stop.englishName}</h2>
	{#if stop.nativeName}
		<NativeText
			classes="mb-3 text-xs md:text-sm font-medium tracking-widest text-base-content/70"
			text={stop.nativeName.toUpperCase()}
			languageCode={language.code}
			isLanguageRTL={language.rtl}
		/>
	{/if}

	<p class="mb-5 text-base leading-relaxed font-light text-base-content/80 md:text-lg">
		{stop.description}
	</p>

	<div class="mb-5 flex flex-col items-stretch justify-start gap-2.5 sm:flex-row sm:items-center">
		{@render ActionButtons()}
	</div>

	{const images = $derived<ImageDetails[]>([...(stop.images || []), ...(stop.primary?.images || [])])}
	{#if images.length > 0}
		<ImageGallery
			classes="h-[42vh] md:h-[45vh]"
			{images}
			ariaLabel={`View "${data.tour.feature.englishName}" gallery`}
			eagerLoad={false}
		/>
	{/if}

	<PlaceExtras name={stop.englishName} place={stop.primary?.place ?? stop.place} showMap={false} />

	{#snippet ActionButtons()}
		<a
			class="btn gap-2 rounded-full border border-base-content/15 bg-transparent font-mono text-base-content/80 transition-all duration-200 btn-sm hover:border-base-content/25 hover:bg-base-content/5 hover:text-base-content focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary md:btn-md"
			href={getStreetViewUrl(stop.place.coordinates)}
			target="_blank"
			rel="noopener noreferrer"
		>
			<IconStreetView class="text-base opacity-70 md:text-lg" />
			<span>street view</span>
		</a>

		{#if stop.primary}
			{const cityFeatureNav = $derived<NavLink>(
				UrlUtil.toCityFeature({ location, type: stop.primary.type, featureSlug: stop.primary.slug })
			)}
			<a
				class="group btn gap-2 rounded-full border-0 bg-primary font-mono text-primary-content shadow-sm transition-all duration-200 btn-sm hover:bg-primary/90 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:translate-y-0 md:btn-md"
				href={`/${cityFeatureNav.href}`}
				data-sveltekit-preload-data={cityFeatureNav.preloadData}
				data-sveltekit-preload-code={cityFeatureNav.preloadCode}
			>
				<span>
					{#if CITY_FEATURE_PAGE_PER_ITEM_TYPES.includes(stop.primary.type)}
						view detailed guide
					{:else}
						view stop details
					{/if}
				</span>
				<IconArrowRight class="text-base transition-transform duration-200 group-hover:translate-x-0.5" />
			</a>
		{/if}
	{/snippet}
{/snippet}

{#snippet ToNextStop(segmentToNextStop: RouteSegment)}
	{const timeFormatted = $derived<string>(
		secondsToFormattedHourMinutes(segmentToNextStop.durationSeconds, { formatting: 'medium' })
	)}
	<div
		class="mt-3 flex h-6 items-center gap-2 font-mono text-sm text-base-content/70"
		role="group"
		aria-label={`To next stop: ${timeFormatted}, ${segmentToNextStop.distanceMeters} meters`}
	>
		<span aria-hidden="true">{timeFormatted}</span>
		<span aria-hidden="true" class="text-base-content/30">·</span>
		<span aria-hidden="true">{segmentToNextStop.distanceMeters} m</span>
		<span aria-hidden="true" class="text-base-content/30">·</span>
		<span class="sr-only md:not-sr-only"> to next stop </span>
	</div>
{/snippet}

{#snippet MobileMapButton()}
	<div class="fixed bottom-6 left-1/2 -translate-x-1/2 lg:hidden">
		<button class="btn rounded-full px-8 font-mono shadow-2xl btn-primary" onclick={() => (isMobileMapOpen = true)}>
			<IconMap class="mr-1 text-lg" />
			view route
		</button>
	</div>
{/snippet}

{#snippet MobileMapModal()}
	{@render DarkBackdrop()}

	<div
		class="fixed right-0 bottom-0 left-0 flex h-[85vh] w-full touch-none flex-col overflow-hidden rounded-t-box bg-base-100 shadow-xl lg:hidden"
		role="dialog"
		aria-modal="true"
		transition:fly={{ y: 800, duration: 250, opacity: 1 }}
	>
		<div class="flex items-center justify-between px-5 pt-3 pb-3">
			<div class="flex flex-row items-center justify-center gap-4">
				<div class="flex flex-col">
					<h3 class="text-lg">Route Map</h3>
					<span class="text-xs font-bold tracking-widest text-primary">
						STOP {activeStopIndex + 1} OF {stops.length}
					</span>
				</div>
				{#if !player.isPlaying}
					<div>
						<button
							class="btn btn-circle btn-ghost btn-sm"
							popovertarget="map-explanation-mobile"
							aria-label="Show map explanation"
						>
							<IconQuestionMark class="text-lg opacity-70" />
						</button>
						<div popover id="map-explanation-mobile">
							<p>{mapExplanation}</p>
						</div>
					</div>
				{/if}
			</div>

			<button class="btn btn-circle btn-ghost btn-sm" onclick={() => (isMobileMapOpen = false)}>
				<IconClose class="text-xl" />
			</button>
		</div>

		<div class="relative flex-1">
			{@render MapContainer()}
		</div>
	</div>

	{#snippet DarkBackdrop()}
		<div
			class="fixed inset-0 bg-black/40 backdrop-blur-sm lg:hidden"
			transition:fade={{ duration: 200 }}
			onclick={() => (isMobileMapOpen = false)}
			role="button"
			tabindex="-1"
			aria-label="Close map"
			onkeydown={() => {}}
		></div>
	{/snippet}
{/snippet}

{#snippet MapContainer()}
	<div
		bind:this={mapWrapperRef}
		class={{
			'overflow-hidden transition-all duration-300': true,
			'relative h-full w-full': !isVirtualFullscreen,
			'fixed inset-0 z-50 h-dvh w-screen': isVirtualFullscreen,
			'lg:rounded-box': !player.isPlaying
		}}
	>
		{#if browser && isMounted && shouldLoadMapComponent}
			{#await import('$lib/components/ui/map/TourMap.svelte') then { default: TourMap }}
				<TourMap
					{stops}
					{route}
					{activeStopIndex}
					{player}
					padding={60}
					pitch={45}
					onStopClick={handleMapStopClick}
					onMapReady={(map) => {
						mapInstance = map;
					}}
				>
					{#if player.isPlaying && mapInstance}
						{#key tour.feature.slug}
							<GpsTourPlayer
								map={mapInstance}
								{location}
								{route}
								{stops}
								initialDistance={player.distance}
								cache={wikiCache}
								onProgressChange={(d) => (player.distance = d)}
								onClose={exitTour}
							/>
						{/key}
					{/if}
				</TourMap>

				{#if mapInstance && !player.isPlaying}
					{@render PlayButton()}
				{/if}
			{/await}
		{:else}
			<div class="absolute inset-0 flex items-center justify-center">
				<span class="loading mr-2 loading-spinner text-primary"></span>
				<span class="font-medium text-base-content/70">Loading route map...</span>
			</div>
		{/if}
	</div>

	{#snippet PlayButton()}
		{const THRESHOLD = 50}
		{const totalDistance = $derived<number>(tour.route.distanceMeters)}
		{const noun = $derived<string>(CITY_FEATURE_TYPE_SINGLE_LABELS[featureType])}
		{const mode = $derived<'start' | 'resume' | 'replay'>(
			player.distance >= totalDistance - THRESHOLD ? 'replay' : player.distance > THRESHOLD ? 'resume' : 'start'
		)}
		{const progress = $derived<number>(Math.min(1, Math.max(0, player.distance / totalDistance)))}
		{const title = $derived<string>(
			mode === 'resume' ? `Resume 3D ${noun}` : mode === 'replay' ? `Replay 3D ${noun}` : `Play 3D ${noun}`
		)}
		{const subtitle = $derived<string>(
			mode === 'resume'
				? `${Math.round(progress * 100)}% complete`
				: mode === 'replay'
					? 'You reached the end'
					: `${(totalDistance / 1000).toFixed(1)} km, camera follows the route`
		)}

		<div
			class="pointer-events-none absolute inset-x-0 z-30 flex justify-center px-4"
			style="bottom: max(1.25rem, env(safe-area-inset-bottom));"
		>
			<div class="pointer-events-auto flex items-center gap-2">
				<button
					type="button"
					onclick={() => startTour(false)}
					aria-label={`${title}, ${subtitle}`}
					class="btn relative h-auto min-h-12 gap-3 overflow-hidden rounded-full py-1.5 pr-5 pl-1.5 shadow-2xl transition-transform btn-primary hover:scale-[1.03] active:scale-[0.98] sm:min-h-14 sm:pr-6 sm:pl-2"
				>
					{@render Puck()}
					{@render Label()}
					{#if mode === 'resume'}
						{@render ProgressLine()}
					{/if}
				</button>

				{#if player.distance > THRESHOLD}
					{@render RestartButton()}
				{/if}
			</div>
		</div>

		{#snippet Puck()}
			<span class="relative flex size-9 shrink-0 items-center justify-center sm:size-10">
				{#if mode === 'start'}
					<span
						class="absolute inset-0 rounded-full bg-primary-content/30 motion-safe:animate-ping motion-safe:[animation-duration:2.4s]"
						aria-hidden="true"
					></span>
				{/if}
				<span class="relative flex size-full items-center justify-center rounded-full bg-primary-content/15">
					<IconPlay class="translate-x-px text-lg text-primary-content sm:text-xl" />
				</span>
			</span>
		{/snippet}

		{#snippet Label()}
			<span class="flex flex-col items-start text-left leading-tight">
				<span class="text-sm font-semibold sm:text-base">{title}</span>
				<span class="text-xs font-normal opacity-70 sm:text-[0.8rem]">{subtitle}</span>
			</span>
		{/snippet}

		{#snippet ProgressLine()}
			<progress
				class="progress absolute inset-x-0 bottom-0 h-0.5 rounded-none progress-primary text-primary-content/90 [&::-webkit-progress-bar]:bg-primary-content/20"
				value={progress * 100}
				max="100"
				aria-hidden="true"
			></progress>
		{/snippet}

		{#snippet RestartButton()}
			<button
				class="btn btn-circle shadow-xl btn-neutral btn-md sm:btn-lg"
				type="button"
				onclick={() => startTour(true)}
				aria-label="Restart from the beginning"
				title="Restart from the beginning"
			>
				<IconRestart class="text-base sm:text-lg" />
			</button>
		{/snippet}
	{/snippet}
{/snippet}

{#snippet OtherTourTypesSection()}
	<section class="mt-14 md:mt-24">
		<div
			class="mb-6 flex flex-col items-center justify-center border-b border-base-content/10 pb-4 md:flex-row md:items-end md:justify-between"
		>
			<h2 class="flex flex-col items-center justify-center gap-2 text-xl md:flex-row md:text-3xl">
				<IconMapMarkerPath class="text-xl text-primary md:text-3xl" />
				<span>Alternative Ways to Tour the City</span>
			</h2>
			<span> Switch your pace. Discover curated routes by bike, foot, or self-guided exploration. </span>
		</div>

		<div class="grid grid-cols-1 gap-8 md:grid-cols-2">
			{#each otherTypesOfRoutes as item (item.id)}
				{@const Icon = CITY_FEATURE_TYPE_ICON_MAP[item.type]}
				{@const typeLabel = CITY_FEATURE_TYPE_LABELS[item.type]}
				{@const allRoutesNav = UrlUtil.toCityFeatures({ location, type: item.type })}

				<div class="flex flex-col gap-3">
					<div class="flex items-center justify-between px-1">
						<span class="flex items-center gap-1.5 text-sm font-bold tracking-widest text-primary uppercase">
							<Icon class="text-base" />
							<span>{typeLabel}</span>
						</span>
					</div>

					<ExpandedFeatureCard feature={item} images={item.images} {location} ctaText="view route" />

					<a
						class="group btn rounded-full font-mono btn-md btn-primary"
						href={`/${allRoutesNav.href}`}
						data-sveltekit-preload-data={allRoutesNav.preloadData}
						data-sveltekit-preload-code={allRoutesNav.preloadCode}
					>
						<span>explore {typeLabel.toLowerCase()} in {location.city.label}</span>
						<IconArrowRight class="transition-transform duration-200 group-hover:translate-x-1" />
					</a>
				</div>
			{/each}
		</div>
	</section>
{/snippet}

<style>
	[popover] {
		border: none !important;
		padding: 20px !important;
		background: transparent !important;
		outline: none !important;
		margin: auto !important;
		color: aliceblue !important;
	}
	[popover]::backdrop {
		background-color: rgba(0, 0, 0, 0.5) !important;
		backdrop-filter: blur(4px) !important;
	}
</style>
